import { useEffect, useState } from 'react';
import { api, ErroApi } from '../../api';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio, quando,
} from '../../componentes/ui';
import type { Escritorio, Instalacao } from '../../tipos';

export function Instalacoes() {
  const [lista, setLista] = useState<Instalacao[] | null>(null);
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [erro, setErro] = useState('');
  const [nova, setNova] = useState(false);
  const [chaveNova, setChaveNova] = useState<{ nome: string; chave: string } | null>(null);

  async function carregar() {
    try {
      const { instalacoes } = await api.instalacoes();
      setLista(instalacoes);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as instalações.');
    }
  }

  useEffect(() => {
    void carregar();
    api.escritorios().then((r) => setEscritorios(r.escritorios)).catch(() => {});
  }, []);

  async function alternar(i: Instalacao) {
    try {
      await api.alterarInstalacao(i.id, { ativo: !i.ativo });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui alterar.');
    }
  }

  const semEscritorio = (lista || []).filter((i) => !i.cadastroAutomatico && i.ativo);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Instalações</h1>
          <p>
            Cada máquina de cliente onde o agente roda tem a sua credencial. Uma instalação cobre
            todas as empresas emitentes daquela máquina.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setNova(true)}>Nova instalação</button>
      </header>

      <Erro>{erro}</Erro>

      {semEscritorio.length > 0 && (
        <p className="nota">
          {semEscritorio.length} instalação(ões) sem escritório fixo. Nelas a empresa só entra pelo vínculo
          que o sistema emissor envia (como o GestorIA) ou por cadastro à mão; nota de CNPJ sem vínculo é
          recusada. Se for um agente de loja, defina o escritório dele.
        </p>
      )}

      {!lista ? <Carregando /> : lista.length === 0 ? (
        <Vazio
          titulo="Nenhuma instalação"
          descricao="Crie uma para cada máquina onde o agente vai rodar. A chave aparece uma única vez."
          acao={<button className="btn btn-primary btn-sm" onClick={() => setNova(true)}>Criar a primeira</button>}
        />
      ) : (
        <Tabela
          itens={lista}
          chaveDe={(i) => i.id}
          classeLinha={(i) => (i.ativo ? undefined : 'linha-inativa')}
          colunas={[
            { rotulo: 'Instalação', principal: true, celula: (i) => <strong>{i.nome}</strong> },
            {
              rotulo: 'Escritório',
              fraco: true,
              celula: (i) => i.escritorio || <span className="td-alerta">sem escritório</span>,
            },
            { rotulo: 'Chave', fraco: true, mono: true, celula: (i) => `inst_${i.chavePrefixo}…` },
            { rotulo: 'Último contato', fraco: true, celula: (i) => quando(i.ultimoContatoEm) },
            { rotulo: 'Lotes (7d)', numero: true, celula: (i) => i.lotes7d },
            {
              rotulo: 'Ações',
              acoes: true,
              celula: (i) => (
                <>
                  {!i.ativo && <Selo tipo="inativo" />}
                  <button className="link-botao" onClick={() => alternar(i)}>
                    {i.ativo ? 'Desativar' : 'Reativar'}
                  </button>
                </>
              ),
            },
          ]}
        />
      )}

      {nova && (
        <NovaInstalacao
          escritorios={escritorios}
          onFechar={() => setNova(false)}
          onCriada={(nome, chave) => { setNova(false); setChaveNova({ nome, chave }); void carregar(); }}
        />
      )}

      {chaveNova && <ChaveGerada {...chaveNova} onFechar={() => setChaveNova(null)} />}
    </div>
  );
}

function NovaInstalacao({
  escritorios, onFechar, onCriada,
}: {
  escritorios: Escritorio[];
  onFechar: () => void;
  onCriada: (nome: string, chave: string) => void;
}) {
  const [nome, setNome] = useState('');
  const [escritorioId, setEscritorioId] = useState<number | ''>(escritorios[0]?.id ?? '');
  // Softhouse que vincula cada empresa ao seu escritorio (PUT /api/v1/empresas),
  // como o GestorIA: uma instalacao so para as notas de todas as lojas.
  const [porEmpresa, setPorEmpresa] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro('');
    setSalvando(true);
    try {
      const r = await api.criarInstalacao(porEmpresa ? { nome, semEscritorio: true } : { nome, escritorioId: Number(escritorioId) });
      onCriada(r.instalacao.nome, r.chave);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui criar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Nova instalação" descricao="A credencial que o agente vai usar naquela máquina." onFechar={onFechar}>
      <Erro>{erro}</Erro>
      <Campo rotulo="Nome" ajuda="O que vai te ajudar a reconhecer depois — normalmente o nome do cliente.">
        <input className="input" value={nome} onChange={(e) => setNome(e.target.value)}
          placeholder="Comércio Vale Verde — matriz" autoFocus />
      </Campo>
      <label className="nota" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={porEmpresa} onChange={(e) => setPorEmpresa(e.target.checked)} />
        <span>
          <b>Sistema que informa o escritório de cada empresa</b> (como o GestorIA). Sem escritório fixo:
          a empresa entra pelo vínculo que o sistema envia antes da primeira nota.
        </span>
      </label>
      {!porEmpresa && (
        <Campo
          rotulo="Escritório"
          ajuda="Obrigatório: é por ele que a empresa emitente será cadastrada sozinha quando o primeiro XML chegar."
        >
          <select className="input" value={escritorioId}
            onChange={(e) => setEscritorioId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Escolha…</option>
            {escritorios.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
        </Campo>
      )}

      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar}
          disabled={salvando || nome.trim().length < 2 || (!porEmpresa && !escritorioId)}>
          {salvando ? <span className="spinner" /> : 'Criar e gerar a chave'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * A chave em claro existe uma vez só — o banco guarda o hash. Por isso
 * esta tela é um passo separado, com o aviso em cima e sem fechar por
 * clique fora sem querer: perder a chave aqui significa apagar a
 * instalação e criar outra.
 */
function ChaveGerada({ nome, chave, onFechar }: { nome: string; chave: string; onFechar: () => void }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(chave);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setCopiado(false);
    }
  }

  return (
    <Modal titulo="Anote a chave agora" onFechar={onFechar} largura={620}>
      <p className="modal-aviso">
        Esta é a única vez que a chave aparece. O portal guarda só o hash dela — se você perder,
        não dá para recuperar: apaga esta instalação e cria outra.
      </p>

      <div className="chave-caixa">
        <code>{chave}</code>
      </div>

      <button className="btn btn-secondary" onClick={copiar} style={{ marginBottom: 18 }}>
        {copiado ? '✓ Copiada' : 'Copiar chave'}
      </button>

      <p className="campo-ajuda" style={{ marginTop: 0 }}>
        Instalação <strong>{nome}</strong>. No cliente, rode o <code>Conti9AgenteSetup.exe</code> e
        cole esta chave quando o instalador pedir — ele testa contra o portal na hora.
      </p>

      <div className="modal-acoes">
        <button className="btn btn-primary" onClick={onFechar}>Já anotei</button>
      </div>
    </Modal>
  );
}
